import React, { useState, useEffect } from 'react';
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  PlusIcon,
  DocumentChartBarIcon,
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { apiDelete, apiGet, apiPost, apiPut } from '../utils/api';
import TransactionFormModal from '../components/vsla/TransactionFormModal';
import OfficerManagementCard from '../components/vsla/OfficerManagementCard';
import VSLAHealthMetrics from '../components/vsla/VSLAHealthMetrics';
import MemberFinancialTable from '../components/vsla/MemberFinancialTable';
import AuditChecklistModal from '../components/vsla/AuditChecklistModal';
import QuickActions from '../components/vsla/QuickActions';
import { formatCurrency } from '../utils/formatters';

interface VSLAGroup {
  id: number;
  name: string;
  member_count: number;
  active_members: number;
  cohort_id?: number;
  cohort_name?: string;
  status: string;
  share_price: number;
  meeting_day: string;
  meeting_location?: string;
  cycle_start_date: string;
  cycle_end_date: string;
  next_election_date: string;
  next_audit_date: string;
  health_score?: number;
  metrics?: {
    total_savings: number;
    active_loan_portfolio: number;
    social_fund: number;
    maintenance_fund: number;
    total_members: number;
    active_members: number;
    repayment_rate: number;
    default_rate: number;
    attendance_rate: number;
    health_score: number;
    active_loans_count: number;
    share_price: number;
    share_value: number;
  };
}

interface Transaction {
  id: number;
  member_name?: string;
  member_number?: string;
  type: string;
  amount: number;
  shares_involved?: number;
  meeting_date?: string;
  created_at?: string;
  description: string;
  status: string;
  loan_number?: string;
}

interface Meeting {
  id: number;
  meeting_number: number;
  meeting_date: string;
  meeting_type: string;
  members_present: number;
  total_members: number;
  attendance_rate: number;
  total_savings_collected: number;
  status: string;
}

const VSLA: React.FC = () => {
  const [vslaGroups, setVslaGroups] = useState<VSLAGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [officers, setOfficers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'groups' | 'overview' | 'meetings' | 'loans' | 'analytics'>('overview');
  const [memberRefreshKey, setMemberRefreshKey] = useState(0);

  // Modals
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  
  const [createGroupForm, setCreateGroupForm] = useState({
    name: '', meeting_day: 'Monday', share_price: '500',
    meeting_location: '', meeting_time: '14:00', seed_capital: '0'
  });
  const [createGroupLoading, setCreateGroupLoading] = useState(false);

  const [editGroupForm, setEditGroupForm] = useState({
    id: 0,
    name: '',
    status: 'active',
    meeting_day: 'Monday',
    meeting_time: '14:00',
    meeting_location: '',
    share_price: '500',
    seed_capital: '0',
    cycle_start_date: '',
  });
  const [editGroupLoading, setEditGroupLoading] = useState(false);

  useEffect(() => {
    fetchVSLAGroups();
  }, []);

  const handleCreateGroup = async () => {
    if (!createGroupForm.name.trim()) return;
    setCreateGroupLoading(true);
    try {
      const created: any = await apiPost('/api/vsla', {
        name: createGroupForm.name,
        cohort_id: null,
        meeting_day: createGroupForm.meeting_day,
        share_price: parseFloat(createGroupForm.share_price) || 500,
        meeting_location: createGroupForm.meeting_location,
        meeting_time: createGroupForm.meeting_time,
        seed_capital: parseFloat(createGroupForm.seed_capital) || 0,
        cycle_start_date: new Date().toISOString().split('T')[0]
      });
      setIsCreateGroupOpen(false);
      setCreateGroupForm({
        name: '', meeting_day: 'Monday', share_price: '500',
        meeting_location: '', meeting_time: '14:00', seed_capital: '0'
      });
      await fetchVSLAGroups();
      if (created?.id) {
        setSelectedGroupId(created.id);
        await refreshGroupData(created.id);
      }
    } catch (error) {
      console.error('Failed to create VSLA group:', error);
    } finally {
      setCreateGroupLoading(false);
    }
  };

  useEffect(() => {
    if (selectedGroupId) {
      refreshGroupData(selectedGroupId);
    }
  }, [selectedGroupId]);

  const fetchVSLAGroups = async () => {
    try {
      const data = await apiGet<VSLAGroup[]>('/api/vsla');
      setVslaGroups(data);
      if (data.length > 0) {
        if (!selectedGroupId) {
          setSelectedGroupId(data[0].id);
        } else if (!data.some(g => g.id === selectedGroupId)) {
          setSelectedGroupId(data[0].id);
        }
      } else {
        setSelectedGroupId(null);
      }
    } catch (error) {
      console.error("Failed to fetch VSLA groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshGroupData = async (id: number) => {
    try {
      const [grp, txns, offs, mtgs] = await Promise.all([
        apiGet<VSLAGroup>(`/api/vsla/${id}`),
        apiGet<Transaction[]>(`/api/vsla/${id}/transactions`),
        apiGet<any[]>(`/api/vsla/${id}/officers`),
        apiGet<Meeting[]>(`/api/vsla/${id}/meetings`).catch(() => [])
      ]);
      setVslaGroups(prev => prev.map(g => g.id === id ? grp : g));
      setTransactions(txns);
      setOfficers(offs);
      setMeetings(mtgs);
    } catch (error) {
      console.error("Failed to refresh group data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransaction = async (data: any) => {
    if (!selectedGroupId) return;
    await apiPost(`/api/vsla/${selectedGroupId}/transactions`, data);
    await refreshGroupData(selectedGroupId);
  };

  const handleOpenEditGroup = (group: VSLAGroup) => {
    setEditGroupForm({
      id: group.id,
      name: group.name || '',
      status: (group.status as any) || 'active',
      meeting_day: group.meeting_day || 'Monday',
      meeting_time: (group as any).meeting_time || '14:00',
      meeting_location: group.meeting_location || '',
      share_price: String(group.share_price || 500),
      seed_capital: String((group as any).seed_capital || 0),
      cycle_start_date: group.cycle_start_date ? String(group.cycle_start_date).split('T')[0] : '',
    });
    setIsEditGroupOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editGroupForm.id || !editGroupForm.name.trim()) return;
    setEditGroupLoading(true);
    try {
      await apiPut(`/api/vsla/${editGroupForm.id}`, {
        name: editGroupForm.name,
        status: editGroupForm.status,
        cohort_id: null,
        meeting_day: editGroupForm.meeting_day,
        meeting_time: editGroupForm.meeting_time,
        meeting_location: editGroupForm.meeting_location,
        share_price: parseFloat(editGroupForm.share_price) || 500,
        seed_capital: parseFloat(editGroupForm.seed_capital) || 0,
        cycle_start_date: editGroupForm.cycle_start_date || undefined,
      });

      await fetchVSLAGroups();

      if (selectedGroupId === editGroupForm.id) {
        await refreshGroupData(editGroupForm.id);
      }

      setIsEditGroupOpen(false);
    } catch (error) {
      console.error('Failed to update VSLA group:', error);
    } finally {
      setEditGroupLoading(false);
    }
  };

  const handleDeleteGroup = async (group: VSLAGroup) => {
    const ok = window.confirm(`Delete VSLA group "${group.name}"? This will remove the group and its related records.`);
    if (!ok) return;

    try {
      await apiDelete(`/api/vsla/${group.id}`);
      await fetchVSLAGroups();
      if (selectedGroupId === group.id) {
        const next = vslaGroups.find(g => g.id !== group.id);
        setSelectedGroupId(next ? next.id : null);
      }
    } catch (error) {
      console.error('Failed to delete VSLA group:', error);
    }
  };

  const selectedGroup = vslaGroups.find(g => g.id === selectedGroupId);

  // Calculate comprehensive metrics
  const [calculatedMetrics, setCalculatedMetrics] = useState<any>(null);

  useEffect(() => {
    if (selectedGroup) {
      const baseMetrics = selectedGroup.metrics || {
        total_savings: 0,
        active_loan_portfolio: 0,
        social_fund: 0,
        maintenance_fund: 0,
        total_members: selectedGroup.member_count || 0,
        active_members: selectedGroup.active_members || 0,
        repayment_rate: 0,
        default_rate: 0,
        attendance_rate: 0,
        health_score: 0,
        active_loans_count: 0,
        share_price: selectedGroup.share_price || 500,
        share_value: 550
      };

      // Calculate real metrics from transaction data
      const totalSavings = transactions
        .filter(t => ['share_purchase', 'savings_deposit'].includes(t.type))
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const totalLoansIssued = transactions
        .filter(t => t.type === 'loan_disbursement')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const totalRepayments = transactions
        .filter(t => t.type === 'loan_repayment')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const repaymentRate = totalLoansIssued > 0
        ? Math.min(100, (totalRepayments / totalLoansIssued) * 100)
        : 100;
      
      const healthScore = Math.round(
        (repaymentRate * 0.4) + 
        (baseMetrics.attendance_rate * 0.3) + 
        ((baseMetrics.active_members >= 15 && baseMetrics.active_members <= 25 ? 100 : 70) * 0.2) +
        (80 * 0.1)
      );

      setCalculatedMetrics({
        ...baseMetrics,
        total_savings: Math.max(totalSavings, baseMetrics.total_savings),
        active_loan_portfolio: Math.max(0, totalLoansIssued - totalRepayments),
        repayment_rate: Math.round(repaymentRate * 100) / 100,
        health_score: healthScore
      });
    }
  }, [selectedGroup, transactions]);

  const metrics = calculatedMetrics || {
    total_savings: 0,
    active_loan_portfolio: 0,
    social_fund: 0,
    maintenance_fund: 0,
    total_members: 0,
    active_members: 0,
    repayment_rate: 0,
    default_rate: 0,
    attendance_rate: 0,
    health_score: 0,
    active_loans_count: 0,
    share_price: 500,
    share_value: 550
  };

  const filteredGroups = vslaGroups;

  if (loading && !selectedGroup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-xl">
                <BanknotesIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">VSLA Management System</h1>
                <p className="text-sm text-gray-600 font-medium">Village Savings & Loan Association - CARE International Standards</p>
              </div>
            </div>
            
            {selectedGroup && (
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <BuildingLibraryIcon className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold">{selectedGroup.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="w-4 h-4 text-green-600" />
                  <span>{metrics.active_members}/{metrics.total_members} Active Members</span>
                </div>
                <div className="flex items-center gap-2">
                  <CurrencyDollarIcon className="w-4 h-4 text-yellow-600" />
                  <span>{formatCurrency(metrics.total_savings)} Total Savings</span>
                </div>
                <div className="flex items-center gap-2">
                  <ChartBarIcon className="w-4 h-4 text-purple-600" />
                  <span className={`font-bold ${
                    metrics.health_score >= 80 ? 'text-green-600' :
                    metrics.health_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    Health Score: {metrics.health_score}/100
                  </span>
                </div>
                {selectedGroup.next_election_date && (
                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="w-4 h-4 text-orange-600" />
                    <span>Next Election: {new Date(selectedGroup.next_election_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            {/* VSLA Group Selector */}
            <div className="bg-white p-1 rounded-xl border border-gray-200 shadow-sm">
              <select
                value={selectedGroupId || ''}
                onChange={(e) => setSelectedGroupId(Number(e.target.value))}
                className="bg-transparent border-none text-blue-600 text-sm font-bold focus:ring-0 cursor-pointer py-2 pl-3 pr-8 min-w-[200px]"
              >
                {filteredGroups.length === 0 && <option value="">No groups found</option>}
                {filteredGroups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.active_members || g.member_count} members)
                  </option>
                ))}
              </select>
            </div>

            <div className="hidden md:block w-px bg-gray-200 my-1 h-8"></div>

            <button
              onClick={() => setIsTransactionModalOpen(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-all font-medium text-sm"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Transaction
            </button>
            
            <button
              onClick={() => setIsMeetingModalOpen(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition-all font-medium text-sm"
            >
              <CalendarDaysIcon className="w-4 h-4 mr-2" />
              Start Meeting
            </button>
            
            <button
              onClick={() => setIsCreateGroupOpen(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg shadow-md hover:bg-purple-700 transition-all font-medium text-sm"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Group
            </button>
          </div>
        </div>

        {/* Health Metrics Dashboard */}
        <VSLAHealthMetrics metrics={metrics} />

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-200">
            {[
              { id: 'groups', label: 'Groups', icon: BuildingLibraryIcon },
              { id: 'overview', label: 'Overview', icon: ChartBarIcon },
              { id: 'meetings', label: 'Meetings', icon: CalendarDaysIcon },
              { id: 'loans', label: 'Loans', icon: CurrencyDollarIcon },
              { id: 'analytics', label: 'Analytics', icon: DocumentChartBarIcon }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center px-6 py-4 text-sm font-semibold transition-all ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* Groups Tab */}
            {activeTab === 'groups' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <BuildingLibraryIcon className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-bold text-gray-800">VSLA Groups</h3>
                    <span className="text-xs font-semibold text-gray-500 bg-white px-2 py-1 rounded border">
                      {filteredGroups.length} groups
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsCreateGroupOpen(true)}
                      className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg shadow-sm hover:bg-purple-700 transition-all font-medium text-sm"
                    >
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Create Group
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Group</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Members</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Meeting</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Share Price</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 text-sm">
                      {filteredGroups.map((g) => {
                        const membersCount = Number(g.active_members ?? g.member_count ?? 0);
                        return (
                          <tr key={g.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="font-semibold text-gray-900">{g.name}</div>
                              <div className="text-xs text-gray-500">ID: {g.id}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`px-2 py-1 text-xs rounded-full border ${membersCount > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                {membersCount}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-700">
                              <div className="flex items-center gap-2">
                                <CalendarDaysIcon className="w-4 h-4 text-gray-400" />
                                <span>{g.meeting_day || '—'}{(g as any).meeting_time ? ` • ${(g as any).meeting_time}` : ''}</span>
                              </div>
                              {g.meeting_location && (
                                <div className="text-xs text-gray-500 truncate max-w-[240px]">{g.meeting_location}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-gray-700">
                              {formatCurrency(Number(g.share_price || 500))}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs rounded-full border ${String(g.status || 'active') === 'active' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                {String(g.status || 'active')}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              <button
                                onClick={() => { setSelectedGroupId(g.id); setActiveTab('overview'); }}
                                className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleOpenEditGroup(g)}
                                className="ml-2 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteGroup(g)}
                                className="ml-2 px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredGroups.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                            No VSLA groups found for this filter.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">

                  {selectedGroupId && <MemberFinancialTable key={`${selectedGroupId}-${memberRefreshKey}`} vslaId={selectedGroupId} />}

                  {/* Transaction Ledger */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                      <h3 className="text-lg font-bold text-gray-800">Recent Transactions</h3>
                      <button className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center uppercase tracking-wider">
                        <DocumentChartBarIcon className="w-4 h-4 mr-1" />
                        Export Ledger
                      </button>
                    </div>
                    <div className="overflow-x-auto max-h-[500px]">
                      <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Member</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Shares</th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100 text-sm">
                          {transactions.map((txn) => (
                            <tr key={txn.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-3 text-gray-500 whitespace-nowrap">
                                {new Date(txn.meeting_date || txn.created_at || Date.now()).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-3">
                                <div>
                                  <div className="font-medium text-gray-900">{txn.member_name || 'System'}</div>
                                  {txn.member_number && (
                                    <div className="text-xs text-gray-500">{txn.member_number}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-3">
                                <span className={`px-2 py-1 text-xs rounded-full font-bold ${
                                  txn.type === 'share_purchase' ? 'bg-green-100 text-green-700' :
                                  txn.type === 'savings_deposit' ? 'bg-blue-100 text-blue-700' :
                                  txn.type === 'loan_disbursement' ? 'bg-orange-100 text-orange-700' :
                                  txn.type === 'loan_repayment' ? 'bg-purple-100 text-purple-700' :
                                  txn.type === 'social_fund_contribution' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {txn.type.replace(/_/g, ' ').toUpperCase()}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-center text-gray-600">
                                {txn.shares_involved || '-'}
                              </td>
                              <td className="px-6 py-3 text-right font-mono text-gray-700">
                                {formatCurrency(txn.amount)}
                              </td>
                              <td className="px-6 py-3 text-center">
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                  txn.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  txn.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {txn.status.toUpperCase()}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {transactions.length === 0 && (
                            <tr><td colSpan={6} className="p-8 text-center text-gray-400 italic">No recent transactions</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {selectedGroup && (
                    <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-6 text-white shadow-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold">Group Status</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          selectedGroup.status === 'active' ? 'bg-green-500 text-white' :
                          selectedGroup.status === 'forming' ? 'bg-yellow-500 text-white' :
                          'bg-red-500 text-white'
                        }`}>
                          {selectedGroup.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-200">Share Price:</span>
                          <span className="font-semibold">{formatCurrency(selectedGroup.share_price || 500)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-200">Meeting Day:</span>
                          <span className="font-semibold">{selectedGroup.meeting_day}</span>
                        </div>
                        {selectedGroup.meeting_location && (
                          <div className="flex justify-between">
                            <span className="text-blue-200">Location:</span>
                            <span className="font-semibold text-right">{selectedGroup.meeting_location}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-blue-200">Cycle:</span>
                          <span className="font-semibold">
                            {selectedGroup.cycle_start_date ? 
                              `${new Date(selectedGroup.cycle_start_date).getFullYear()} - ${new Date(selectedGroup.cycle_end_date).getFullYear()}` :
                              'Not Set'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-xl p-6 text-white shadow-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <ShieldCheckIcon className="w-6 h-6" />
                      <h3 className="text-lg font-bold">Quarterly Audit</h3>
                    </div>
                    <p className="text-purple-100 text-sm mb-4 opacity-90">
                      Verify cashbooks, passbooks, and ledger integrity.
                    </p>
                    <button
                      onClick={() => setIsAuditModalOpen(true)}
                      className="w-full py-3 bg-white text-purple-900 rounded-lg font-bold text-sm hover:bg-purple-50 transition-all shadow-md"
                    >
                      Start Audit Process
                    </button>
                  </div>

                  <OfficerManagementCard officers={officers} />

                  {selectedGroupId && (
                    <QuickActions
                      vslaId={selectedGroupId}
                      vslaName={selectedGroup?.name || 'VSLA Group'}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Meetings Tab */}
            {activeTab === 'meetings' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">Meeting Management</h3>
                  <button
                    onClick={() => setIsMeetingModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition-all font-medium text-sm"
                  >
                    <CalendarDaysIcon className="w-4 h-4 mr-2" />
                    Start New Meeting
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <h4 className="text-lg font-bold text-gray-800">Recent Meetings</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Meeting #</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Attendance</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Savings</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100 text-sm">
                        {meetings.map((meeting) => (
                          <tr key={meeting.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">#{meeting.meeting_number}</td>
                            <td className="px-6 py-4 text-gray-600">
                              {new Date(meeting.meeting_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs rounded-full font-bold ${
                                meeting.meeting_type === 'regular' ? 'bg-blue-100 text-blue-700' :
                                meeting.meeting_type === 'election' ? 'bg-purple-100 text-purple-700' :
                                meeting.meeting_type === 'audit' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {meeting.meeting_type.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="font-semibold">{meeting.members_present}/{meeting.total_members}</span>
                              <span className="text-xs text-gray-500 ml-1">({meeting.attendance_rate}%)</span>
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-gray-700">
                              {formatCurrency(meeting.total_savings_collected)}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                meeting.status === 'completed' ? 'bg-green-100 text-green-700' :
                                meeting.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {meeting.status.toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {meetings.length === 0 && (
                          <tr><td colSpan={6} className="p-8 text-center text-gray-400 italic">No meetings recorded yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Loans Tab */}
            {activeTab === 'loans' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">Loan Portfolio Management</h3>
                  <div className="flex gap-2">
                    <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-all font-medium text-sm">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      New Loan
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">Active Loans</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{metrics.active_loans_count}</h3>
                      </div>
                      <CurrencyDollarIcon className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">Portfolio Value</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(metrics.active_loan_portfolio)}</h3>
                      </div>
                      <BanknotesIcon className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">Repayment Rate</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{metrics.repayment_rate}%</h3>
                      </div>
                      <ChartBarIcon className="w-8 h-8 text-purple-600" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">Default Rate</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{metrics.default_rate}%</h3>
                      </div>
                      <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <h4 className="text-lg font-bold text-gray-800">Loan Applications & Portfolio</h4>
                  </div>
                  <div className="p-6 text-center text-gray-500">
                    <CurrencyDollarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Loan management interface will be available soon.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">VSLA Analytics & Reporting</h3>
                  <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-all font-medium text-sm">
                    <DocumentChartBarIcon className="w-4 h-4 mr-2" />
                    Export Report
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Savings Growth Trend</h4>
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <ChartBarIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Chart will be available with real data</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Loan Portfolio Quality</h4>
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <CurrencyDollarIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Chart will be available with real data</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Meeting Attendance Trends</h4>
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <UserGroupIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Chart will be available with real data</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Social Fund Impact</h4>
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <BanknotesIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Chart will be available with real data</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <TransactionFormModal
          isOpen={isTransactionModalOpen}
          onClose={() => setIsTransactionModalOpen(false)}
          onSubmit={handleCreateTransaction}
          vslaId={selectedGroupId || 0}
        />

        <AuditChecklistModal
          isOpen={isAuditModalOpen}
          onClose={() => setIsAuditModalOpen(false)}
          vslaName={selectedGroup?.name || 'Group'}
        />

        {/* Create VSLA Group Modal */}
        {isCreateGroupOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Create New VSLA Group</h2>
                  <p className="text-sm text-gray-500">Set up a new Village Savings & Loan Association following CARE standards</p>
                </div>
                <button onClick={() => setIsCreateGroupOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
                  <input
                    type="text"
                    value={createGroupForm.name}
                    onChange={e => setCreateGroupForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Amahoro VSLA Group"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Share Price *</label>
                  <input
                    type="number"
                    value={createGroupForm.share_price}
                    onChange={e => setCreateGroupForm(f => ({ ...f, share_price: e.target.value }))}
                    placeholder="500"
                    min="100"
                    step="50"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Day *</label>
                  <select
                    value={createGroupForm.meeting_day}
                    onChange={e => setCreateGroupForm(f => ({ ...f, meeting_day: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Time</label>
                  <input
                    type="time"
                    value={createGroupForm.meeting_time}
                    onChange={e => setCreateGroupForm(f => ({ ...f, meeting_time: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Location</label>
                  <input
                    type="text"
                    value={createGroupForm.meeting_location}
                    onChange={e => setCreateGroupForm(f => ({ ...f, meeting_location: e.target.value }))}
                    placeholder="e.g. Community Center, Village Hall"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seed Capital</label>
                  <input
                    type="number"
                    value={createGroupForm.seed_capital}
                    onChange={e => setCreateGroupForm(f => ({ ...f, seed_capital: e.target.value }))}
                    placeholder="0"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">CARE International Standards</h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Group size: 15-25 members (optimal)</li>
                  <li>• Share price: Fixed amount</li>
                  <li>• Maximum loan: 3x member's savings</li>
                  <li>• Interest rate: 10% per cycle (configurable)</li>
                  <li>• Meeting frequency: Weekly (recommended)</li>
                </ul>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsCreateGroupOpen(false)}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={createGroupLoading || !createGroupForm.name.trim()}
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createGroupLoading ? 'Creating...' : 'Create VSLA Group'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit VSLA Group Modal */}
        {isEditGroupOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Edit VSLA Group</h2>
                  <p className="text-sm text-gray-500">Update group details, meeting rhythm, and finance setup</p>
                </div>
                <button onClick={() => setIsEditGroupOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
                  <input
                    type="text"
                    value={editGroupForm.name}
                    onChange={e => setEditGroupForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editGroupForm.status}
                    onChange={e => setEditGroupForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Share Price</label>
                  <input
                    type="number"
                    value={editGroupForm.share_price}
                    onChange={e => setEditGroupForm(f => ({ ...f, share_price: e.target.value }))}
                    min="100"
                    step="50"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Day</label>
                  <select
                    value={editGroupForm.meeting_day}
                    onChange={e => setEditGroupForm(f => ({ ...f, meeting_day: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Time</label>
                  <input
                    type="time"
                    value={editGroupForm.meeting_time}
                    onChange={e => setEditGroupForm(f => ({ ...f, meeting_time: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Location</label>
                  <input
                    type="text"
                    value={editGroupForm.meeting_location}
                    onChange={e => setEditGroupForm(f => ({ ...f, meeting_location: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seed Capital</label>
                  <input
                    type="number"
                    value={editGroupForm.seed_capital}
                    onChange={e => setEditGroupForm(f => ({ ...f, seed_capital: e.target.value }))}
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Start Date</label>
                  <input
                    type="date"
                    value={editGroupForm.cycle_start_date}
                    onChange={e => setEditGroupForm(f => ({ ...f, cycle_start_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsEditGroupOpen(false)}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateGroup}
                  disabled={editGroupLoading || !editGroupForm.name.trim()}
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editGroupLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Meeting Modal */}
        {isMeetingModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Start VSLA Meeting</h2>
                  <p className="text-sm text-gray-500">Begin a new meeting session</p>
                </div>
                <button onClick={() => setIsMeetingModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
                    <option value="regular">Regular Meeting</option>
                    <option value="emergency">Emergency Meeting</option>
                    <option value="election">Election Meeting</option>
                    <option value="audit">Audit Meeting</option>
                    <option value="share_out">Share-out Meeting</option>
                  </select>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>Meeting Checklist:</strong> Ensure cash box keys are available, attendance sheet is ready, and all officers are present.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsMeetingModalOpen(false)}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setIsMeetingModalOpen(false);
                    window.open('/dashboard/vsla', '_blank');
                  }}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                >
                  Start Meeting
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VSLA;
